import React, { useState, useEffect } from "react";
import {
  Building2,
  Plus,
  Users,
  Trash2,
  UserPlus,
  Shield,
  AlertCircle,
} from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import type {
  Organization,
  OrganizationUser,
} from "../../types";
import { OrganizationRole } from "@secure-caseworker/shared";
import { Modal } from "../../components/common/ui/modal";
import { useAlert } from "../../hooks/useAlert";
import Button from "../../components/common/ui/button/Button";
import Label from "../../components/common/form/Label";
import Input from "../../components/common/form/input/InputField";

interface SuperadminDashboardProps {}

export const SuperadminDashboard: React.FC<SuperadminDashboardProps> = ({}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [orgMembers, setOrgMembers] = useState<OrganizationUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const { setAlert } = useAlert();

  // Create organization form
  const [orgName, setOrgName] = useState("");
  const [orgDescription, setOrgDescription] = useState("");

  // Add member form
  const [memberEmail, setMemberEmail] = useState("");
  const [memberRole, setMemberRole] = useState<OrganizationRole>(
    "admin" as OrganizationRole
  );

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      loadOrgMembers(selectedOrg.id);
    }
  }, [selectedOrg]);

  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const orgs = await apiClient.listAllOrganizations();
      setOrganizations(orgs);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to load organizations",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadOrgMembers = async (orgId: string) => {
    try {
      const members = await apiClient.listOrganizationMembers(orgId);
      setOrgMembers(members);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to load members",
      });
    }
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiClient.createOrganizationAsSuperadmin(orgName, orgDescription);
      setAlert({
        variant: "success",
        title: "Success",
        message: "Organization created successfully",
      });
      setShowCreateOrgModal(false);
      setOrgName("");
      setOrgDescription("");
      loadOrganizations();
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to create organization",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) return;

    setLoading(true);
    try {
      await apiClient.createInvitation(selectedOrg.id, memberEmail, memberRole);
      setAlert({
        variant: "success",
        title: "Success",
        message: "Invitation sent successfully",
      });
      setShowAddMemberModal(false);
      setMemberEmail("");
      setMemberRole("admin" as OrganizationRole);
      loadOrgMembers(selectedOrg.id);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to send invitation",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (orgId: string, userId: string) => {
    if (!confirm("Are you sure you want to remove this member?")) return;

    try {
      await apiClient.removeOrganizationMember(orgId, userId);
      setAlert({
        variant: "success",
        title: "Success",
        message: "Member removed successfully",
      });
      loadOrgMembers(orgId);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to remove member",
      });
    }
  };

  const handleDeleteOrganization = async (orgId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this organization? This action cannot be undone."
      )
    )
      return;

    try {
      await apiClient.deleteOrganization(orgId);
      setAlert({
        variant: "success",
        title: "Success",
        message: "Organization deleted successfully",
      });
      setSelectedOrg(null);
      loadOrganizations();
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete organization",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 flex items-center gap-3 text-title-md2 font-semibold text-black dark:text-white">
            <Shield size={32} />
            Superadmin Dashboard
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage organizations and their administrators
          </p>
        </div>
        <Button
          onClick={() => setShowCreateOrgModal(true)}
          startIcon={<Plus size={20} />}
        >
          Create Organization
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Organizations List */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
          <h2 className="mb-6 flex items-center gap-2 text-title-sm font-semibold text-black dark:text-white">
            <Building2 size={20} />
            Organizations ({organizations.length})
          </h2>

          {loading && organizations.length === 0 ? (
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              Loading...
            </p>
          ) : organizations.length === 0 ? (
            <div className="py-12 text-center">
              <Building2
                size={48}
                className="mx-auto mb-4 opacity-30 text-gray-400"
              />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No organizations yet
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {organizations.map((org) => (
                <div
                  key={org.id}
                  onClick={() => setSelectedOrg(org)}
                  className={`cursor-pointer rounded-lg border p-4 transition-all ${
                    selectedOrg?.id === org.id
                      ? "border-brand-300 bg-brand-50 dark:border-brand-800 dark:bg-brand-900/20"
                      : "border-gray-200 bg-gray-50 hover:border-gray-300 dark:border-gray-800 dark:bg-boxdark/40 dark:hover:border-gray-700"
                  }`}
                >
                  <h3 className="mb-1 font-semibold text-black dark:text-white">
                    {org.name}
                  </h3>
                  {org.description && (
                    <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                      {org.description}
                    </p>
                  )}
                  <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users size={14} />
                      {org.memberCount} members
                    </span>
                    <span>{org.caseCount} cases</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Organization Details */}
        <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
          {!selectedOrg ? (
            <div className="flex h-full flex-col items-center justify-center py-12 text-center">
              <AlertCircle
                size={64}
                className="mb-4 opacity-30 text-gray-400"
              />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Select an organization to view details
              </p>
            </div>
          ) : (
            <>
              {/* Org Header */}
              <div className="mb-6 border-b border-gray-200 pb-6 dark:border-gray-800">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="mb-2 text-title-md font-semibold text-black dark:text-white">
                      {selectedOrg.name}
                    </h2>
                    {selectedOrg.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedOrg.description}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowAddMemberModal(true)}
                      variant="outline"
                      size="sm"
                      startIcon={<UserPlus size={16} />}
                    >
                      Invite Admin
                    </Button>
                    <button
                      onClick={() => handleDeleteOrganization(selectedOrg.id)}
                      className="rounded-lg border border-red-300 bg-red-50 p-2 text-red-600 transition-colors hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                      title="Delete organization"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Members List */}
              <div>
                <h3 className="mb-4 text-lg font-semibold text-black dark:text-white">
                  Members ({orgMembers.length})
                </h3>

                {orgMembers.length === 0 ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 py-8 text-center dark:border-gray-800 dark:bg-boxdark/40">
                    <Users
                      size={48}
                      className="mx-auto mb-4 opacity-30 text-gray-400"
                    />
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      No members yet. Invite an admin to get started.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orgMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-boxdark/40"
                      >
                        <div>
                          <div className="mb-1 font-medium text-black dark:text-white">
                            {member.user?.fullName || member.user?.email}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {member.user?.email}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                            Role: {member.role} • Invited{" "}
                            {new Date(member.invitedAt).toLocaleDateString()}
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            handleRemoveMember(selectedOrg.id, member.userId)
                          }
                          className="rounded-lg border border-red-300 bg-red-50 p-2 text-red-600 transition-colors hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                          title="Remove member"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create Organization Modal */}
      <Modal
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
        title="Create Organization"
      >
        <form onSubmit={handleCreateOrganization} className="space-y-5">
          <div>
            <Label>
              Organization Name <span className="text-error-500">*</span>
            </Label>
            <Input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              required
              placeholder="Acme Corporation"
            />
          </div>

          <div>
            <Label>Description</Label>
            <textarea
              value={orgDescription}
              onChange={(e) => setOrgDescription(e.target.value)}
              placeholder="Optional description"
              rows={3}
              className="h-auto w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowCreateOrgModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create Organization"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Add Member Modal */}
      <Modal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        title="Invite Organization Admin"
        maxWidth="500px"
      >
        <form onSubmit={handleInviteMember} className="space-y-5">
          <div>
            <Label>
              Email Address <span className="text-error-500">*</span>
            </Label>
            <Input
              type="email"
              value={memberEmail}
              onChange={(e) => setMemberEmail(e.target.value)}
              required
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <Label>
              Role <span className="text-error-500">*</span>
            </Label>
            <select
              value={memberRole}
              onChange={(e) =>
                setMemberRole(e.target.value as OrganizationRole)
              }
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="staff">Staff</option>
            </select>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAddMemberModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Sending..." : "Send Invitation"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
